import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Check } from "lucide-react";
import { formatDate, formatMoney } from "@mavatech/shared";
import { Badge, Select } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useFeatures } from "@/lib/useFeatures";
import { FinanceChart } from "./DashboardFinance";

type InvoiceStatus = "sent" | "partially_paid" | "overdue" | string;

function statusBadge(status: InvoiceStatus) {
  if (status === "overdue") return <Badge tone="danger">Überfällig</Badge>;
  if (status === "partially_paid")
    return <Badge tone="warning">Teilzahlung</Badge>;
  if (status === "sent") return <Badge tone="warning">Offen</Badge>;
  return <Badge tone="neutral">{status}</Badge>;
}

/** Kompakte Achsenbeschriftung mit Einheit, z. B. „60 Tsd. €". */
function axisEuro(v: number): string {
  if (Math.abs(v) >= 1000)
    return `${(v / 1000).toLocaleString("de-AT", { maximumFractionDigits: 1 })} Tsd. €`;
  return `${v.toLocaleString("de-AT")} €`;
}

function pctLabel(pct: number): string {
  const sign = pct >= 0 ? "+" : "−";
  return `${sign}${Math.abs(pct).toFixed(1).replace(".", ",")} %`;
}

export function Dashboard() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const me = trpc.auth.me.useQuery();
  const s = trpc.dashboard.summary.useQuery();
  const open = trpc.dashboard.openInvoices.useQuery();
  const tasks = trpc.task.list.useQuery({ status: "open" });
  const setTaskStatus = trpc.task.setStatus.useMutation({
    onSuccess: () => utils.task.list.invalidate(),
  });

  const [months, setMonths] = useState<"6" | "12">("12");

  // Optionales Modul "Ausgaben & Gewinnübersicht": 3 zusätzliche Kacheln + gemeinsames Diagramm
  const { expensesVisible } = useFeatures();
  const ov = trpc.expense.overview.useQuery(undefined, {
    enabled: expensesVisible,
    retry: false,
  });

  const d = s.data;
  const mp = d?.monthlyPayments ?? [];
  const curCents = mp.at(-1)?.cents ?? 0;
  const prevCents = mp.at(-2)?.cents ?? 0;
  const momPct =
    prevCents > 0 ? ((curCents - prevCents) / prevCents) * 100 : null;

  const chartData = mp
    .slice(months === "6" ? -6 : -12)
    .map((m) => ({ name: m.label, euro: m.cents / 100 }));
  const chartEmpty = chartData.every((x) => x.euro === 0);

  const kpis = [
    {
      label: "Umsatz · Jahr (netto)",
      value: d ? formatMoney(d.revenueYtdNet) : "—",
      sub: d ? `${d.paidCount} bezahlte Rechnungen` : "",
    },
    {
      label: "Zahlungseingänge · Monat",
      value: d ? formatMoney(curCents) : "—",
      sub: d ? `Vormonat ${formatMoney(prevCents)}` : "",
      pill:
        momPct === null ? null : (
          <Badge tone={momPct >= 0 ? "success" : "danger"}>
            {pctLabel(momPct)}
          </Badge>
        ),
    },
    {
      label: "Offene Rechnungen",
      value: d ? formatMoney(d.openInvoiceSum) : "—",
      sub: d ? `${d.openInvoiceCount} offen` : "",
      pill:
        d && d.overdueCount > 0 ? (
          <Badge tone="danger">{d.overdueCount} überfällig</Badge>
        ) : null,
    },
    {
      label: "Offene Angebote",
      value: d ? formatMoney(d.openQuoteSum) : "—",
      sub: d ? `${d.openQuoteCount} offen` : "",
      pill: d ? (
        <Badge tone={d.conversionRate >= 50 ? "success" : "warning"}>
          Conversion {d.conversionRate} %
        </Badge>
      ) : null,
    },
    {
      label: "Fällige Wartungsverträge",
      value: d ? formatMoney(d.dueContractSum) : "—",
      sub: d ? `${d.dueContractCount} fällig` : "",
      pill:
        d && d.dueContractCount > 0 ? (
          <Badge tone="warning">{d.dueContractCount} fällig</Badge>
        ) : null,
    },
    ...(expensesVisible
      ? [
          {
            label: "Ausgaben · Monat (netto)",
            value: ov.data ? formatMoney(ov.data.expenseMonthNetCents) : "—",
            sub: ov.data
              ? `Vormonat ${formatMoney(ov.data.expensePrevMonthNetCents)}`
              : "",
            pill: null as React.ReactNode,
          },
          {
            label: "Ausgaben · Jahr (netto)",
            value: ov.data ? formatMoney(ov.data.expenseYearNetCents) : "—",
            sub: ov.data
              ? `Einnahmen netto ${formatMoney(ov.data.incomeYearNetCents)}`
              : "",
            pill: null as React.ReactNode,
          },
          {
            label: "Gewinn · Jahr (netto)",
            value: ov.data ? formatMoney(ov.data.profitYearNetCents) : "—",
            sub: "Einnahmen netto − Ausgaben netto",
            pill: ov.data ? (
              <Badge tone={ov.data.profitYearNetCents >= 0 ? "success" : "danger"}>
                {ov.data.profitYearNetCents >= 0 ? "Plus" : "Minus"}
              </Badge>
            ) : null,
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col h-full">
      <header className="px-8 pt-8 pb-4 flex-shrink-0">
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">
          Dashboard
        </h1>
        <p className="text-sm text-text-tertiary mt-1">
          {me.data?.org.legalName ?? "…"} · Überblick
        </p>
      </header>

      <div className="flex-1 overflow-y-auto px-8 pb-8 space-y-6">
        {d?.kleinunternehmerWarning && (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              d.kleinunternehmerWarning.exceeded
                ? "border-danger/30 bg-danger/10 text-danger"
                : "border-warning/30 bg-warning/10 text-warning"
            }`}
          >
            <strong>Kleinunternehmer-Umsatzgrenze: </strong>
            {d.kleinunternehmerWarning.exceeded
              ? `Der heurige Nettoumsatz (${formatMoney(d.kleinunternehmerWarning.ytdNetCents)}) liegt bereits über der Grenze von ${formatMoney(d.kleinunternehmerWarning.limitCents)}. Bitte mit dem Steuerberater klären — ab Überschreiten kann Umsatzsteuerpflicht rückwirkend/laufend entstehen.`
              : `Der heurige Nettoumsatz (${formatMoney(d.kleinunternehmerWarning.ytdNetCents)}) nähert sich der Grenze von ${formatMoney(d.kleinunternehmerWarning.limitCents)}.`}
          </div>
        )}
        {/* Kennzahlen-Reihe */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {kpis.map((k) => (
            <div key={k.label} className="stat-card">
              <span className="kpi-label">{k.label}</span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="kpi-value">{k.value}</span>
                {k.pill}
              </div>
              <span className="text-2xs text-text-tertiary">{k.sub}</span>
            </div>
          ))}
        </div>

        {/* Hauptdiagramm — bei aktivem Ausgaben-Modul gemeinsam mit Ausgaben und Gewinn */}
        {expensesVisible ? (
          <FinanceChart months={ov.data?.months ?? []} range={months} onRangeChange={setMonths} />
        ) : (
          <>
        <div className="panel">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <h2 className="panel-title">Zahlungseingänge</h2>
              <p className="text-2xs text-text-tertiary mt-0.5">
                Erhaltene Zahlungen je Monat
              </p>
            </div>
            <div className="w-44 flex-shrink-0">
              <Select
                value={months}
                onChange={(e) => setMonths(e.target.value as "6" | "12")}
                options={[
                  { value: "6", label: "Letzte 6 Monate" },
                  { value: "12", label: "Letzte 12 Monate" },
                ]}
              />
            </div>
          </div>
          <div className="h-64">
            {chartEmpty ? (
              <div className="h-full flex items-center justify-center text-sm text-text-tertiary">
                Noch keine Zahlungseingänge erfasst.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 4, right: 8, bottom: 0, left: 8 }}
                >
                  <CartesianGrid
                    stroke="rgb(var(--color-border-subtle))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: "rgb(var(--color-text-tertiary))",
                      fontSize: 11,
                    }}
                    tickLine={false}
                    axisLine={false}
                    dy={6}
                  />
                  <YAxis
                    tick={{
                      fill: "rgb(var(--color-text-tertiary))",
                      fontSize: 11,
                    }}
                    tickLine={false}
                    axisLine={false}
                    width={78}
                    tickFormatter={axisEuro}
                  />
                  <Tooltip
                    cursor={{ fill: "rgb(var(--color-bg-hover))" }}
                    contentStyle={{
                      background: "rgb(var(--color-bg-card))",
                      border: "1px solid rgb(var(--color-border-default))",
                      borderRadius: 10,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: "rgb(var(--color-text-secondary))" }}
                    formatter={(v: number) => [
                      formatMoney(Math.round(v * 100)),
                      "Eingang",
                    ]}
                  />
                  <Bar
                    dataKey="euro"
                    fill="rgb(var(--color-accent))"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={30}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

          </>
        )}

        {/* Aufgaben + offene Rechnungen */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <section className="panel lg:col-span-2">
            <h2 className="panel-title mb-4">Fällige Aufgaben</h2>
            {!tasks.data?.length ? (
              <p className="text-sm text-text-tertiary">
                Keine offenen Aufgaben.
              </p>
            ) : (
              <ul className="divide-y divide-border-subtle -my-2">
                {tasks.data.map((t) => {
                  const overdue = t.dueDate
                    ? new Date(t.dueDate) < new Date()
                    : false;
                  return (
                    <li
                      key={t.id}
                      className="flex items-center gap-3 py-2.5 text-sm"
                    >
                      <button
                        className="group flex items-center justify-center w-5 h-5 rounded-full border-2 border-border hover:border-success hover:bg-success/10 transition-colors shrink-0"
                        onClick={() =>
                          setTaskStatus.mutate({ id: t.id, status: "done" })
                        }
                        title="Als erledigt markieren"
                      >
                        <Check className="w-3 h-3 text-success opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                      <span className="flex-1 text-text-primary leading-snug">
                        {t.title}
                      </span>
                      {t.dueDate && (
                        <span
                          className={
                            overdue
                              ? "text-2xs text-danger"
                              : "text-2xs text-text-tertiary"
                          }
                        >
                          {formatDate(t.dueDate as unknown as string)}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="panel lg:col-span-3">
            <h2 className="panel-title mb-4">Offene Rechnungen</h2>
            {!open.data?.length ? (
              <p className="text-sm text-text-tertiary">
                Keine offenen Rechnungen.
              </p>
            ) : (
              <div className="-mx-5 -mb-5 overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nummer</th>
                      <th>Kunde</th>
                      <th>Fällig</th>
                      <th>Status</th>
                      <th className="text-right">Offen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {open.data.map((i) => (
                      <tr
                        key={i.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/rechnungen/${i.id}`)}
                      >
                        <td className="font-mono text-text-secondary">
                          {i.number ?? "—"}
                        </td>
                        <td className="text-text-primary">{i.customer.name}</td>
                        <td className="text-text-secondary">
                          {formatDate(i.dueDate as unknown as string)}
                        </td>
                        <td>{statusBadge(i.status)}</td>
                        <td className="text-right text-money text-text-primary">
                          {formatMoney(i.openCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
